import type { Result } from "@app/types/shared/result";
import { Err, Ok } from "@app/types/shared/result";

export const defaultCosAgentName = "Friday";

export const cosAgentNameMaxLength = 24;

export const cosAgentNameSuggestions = [
  "Friday",
  "Ada",
  "Juno",
  "Sol",
  "Pilot",
] as const;

// The name is spliced into sentences, email subjects and the
// `:mention[name]{sId=…}` wire format, so brackets and braces would break
// parsing and emoji read badly inline.
const emojiPattern = /\p{Extended_Pictographic}|\u200d|\ufe0f/gu;
const mentionBreakingCharacters = /[[\]{}]/;
const reservedCosAgentNames = new Set([
  "memoair",
  "admin",
  "system",
  "support",
]);

export type CosAgentNameRejection =
  | "empty"
  | "too_long"
  | "reserved"
  | "invalid_characters"
  | "impersonates_user"
  | "taken_by_agent";

export const cosAgentNameRejectionMessages: Record<
  CosAgentNameRejection,
  string
> = {
  empty: "Give me a name so I know what to answer to.",
  too_long: "Keep it under 24 characters.",
  reserved: "That name belongs to MemoAir itself. Pick another.",
  invalid_characters: "Leave out brackets and braces.",
  impersonates_user: "That's your name. Pick one for me instead.",
  taken_by_agent: "Another agent already uses that name. Pick another.",
};

export function normalizeCosAgentName(raw: string): string {
  return raw.replace(emojiPattern, "").replace(/\s+/g, " ").trim();
}

export function validateCosAgentName(
  raw: string,
  { userNames }: { userNames: string[] }
): Result<string, CosAgentNameRejection> {
  const name = normalizeCosAgentName(raw);
  if (name.length === 0) {
    return new Err("empty");
  }
  if (name.length > cosAgentNameMaxLength) {
    return new Err("too_long");
  }
  if (mentionBreakingCharacters.test(name)) {
    return new Err("invalid_characters");
  }
  const lowered = name.toLowerCase();
  if (reservedCosAgentNames.has(lowered)) {
    return new Err("reserved");
  }
  if (userNames.some((userName) => userName.trim().toLowerCase() === lowered)) {
    return new Err("impersonates_user");
  }
  return new Ok(name);
}

export function resolveCosAgentName(stored: string | null | undefined): string {
  if (!stored) {
    return defaultCosAgentName;
  }
  const result = validateCosAgentName(stored, { userNames: [] });
  return result.isOk() ? result.value : defaultCosAgentName;
}
